import { jsPDF as JsPDF } from 'jspdf'
import { toast } from 'react-toastify'
import { logger } from '../index'
import { Attempt, Institution, UserProfile } from '../../models/global.types'
import {
  CertificateInput,
  MeasureTextWidth,
  PDF_INSTITUTION_FONT_SIZE,
  PDF_NAME_SHADOW_OFFSET,
  PDF_PAGE_LONG_SIDE,
  PDF_PAGE_SHORT_SIDE,
  PDF_TEXT_FONT_SIZE,
  buildCertificateInput,
  certificateInputKey,
  certificateNameMaxWidth,
  certificateNameNeedsWrap,
  fitCertificateNameFontSize,
  formatCertificateDate,
  getCertificateBackgroundUrl,
  getCertificateOverlayLayout,
  getCertificateYear,
  getOverlayColor,
  NAME_MINT_SHADOW,
  overlayPctToPdf
} from './certificateConfig'

interface TextElement {
  text: string
  x: number
  y: number
  fontSize: number
  font?: string
  fontStyle?: string
  color?: [number, number, number]
  options?: {
    align?: 'center' | 'left' | 'right' | 'justify'
    maxWidth?: number
  }
}

interface CertificatePdfCache {
  url: string
  key: string
}

let cache: CertificatePdfCache | null = null
let pending: { key: string, promise: Promise<string> } | null = null

async function blobToDataUrl (blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('FileReader result is null'))
      }
    }
    reader.onerror = () => {
      reject(reader.error ?? new Error('Error occurred when reading the certificate image.'))
    }
    reader.readAsDataURL(blob)
  })
}

async function waitForImage (src: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve()
    }
    img.onerror = () => {
      reject(new Error('Failed to load certificate image'))
    }
    img.src = src
  })
}

function pdfMeasureNameWidth (doc: JsPDF): MeasureTextWidth {
  return (text, fontSize) => {
    doc.setFont('Helvetica', 'bold')
    doc.setFontSize(fontSize)
    return doc.getTextWidth(text)
  }
}

function addTextToPDF (doc: JsPDF, textElement: TextElement): void {
  const { text, x, y, fontSize, font, fontStyle, color = [0, 0, 0], options } = textElement
  doc.setFontSize(fontSize)
  if (font !== undefined && fontStyle !== undefined) {
    doc.setFont(font, fontStyle)
  }
  if (color !== null) {
    doc.setTextColor(...color)
  }

  const maxWidth = options?.maxWidth
  if (typeof maxWidth === 'number' && maxWidth > 0) {
    const lines = doc.splitTextToSize(text, maxWidth)
    const lineCount = lines.length
    const yStart = lineCount > 1
      ? y - (lineCount - 1) * fontSize * doc.getLineHeightFactor() / 2
      : y
    doc.text(lineCount > 1 ? lines : text, x, yStart, { align: options?.align ?? 'center' })
    return
  }

  doc.text(text, x, y, options)
}

function buildTextElements (
  input: CertificateInput,
  overlayColor: [number, number, number],
  pdfPageWidth: number,
  pdfPageHeight: number,
  measureWidth: MeasureTextWidth
): TextElement[] {
  const userName = `${input.firstName} ${input.lastName}`.toUpperCase()
  const formattedDate = formatCertificateDate(input.completedAt)
  const layout = getCertificateOverlayLayout(getCertificateYear(input.completedAt))
  const nameFit = { measureWidth, pageWidth: pdfPageWidth }
  const nameFontSize = fitCertificateNameFontSize(userName, nameFit)
  const nameX = overlayPctToPdf(layout.name.xPct, pdfPageWidth)
  const nameY = overlayPctToPdf(layout.name.yPct, pdfPageHeight, nameFontSize)
  const dateX = overlayPctToPdf(layout.date.xPct, pdfPageWidth)
  const dateY = overlayPctToPdf(layout.date.yPct, pdfPageHeight, PDF_TEXT_FONT_SIZE)
  const referenceX = overlayPctToPdf(layout.reference.xPct, pdfPageWidth)
  const nameOptions = certificateNameNeedsWrap(userName, nameFontSize, nameFit)
    ? { align: 'center' as const, maxWidth: certificateNameMaxWidth(pdfPageWidth) }
    : { align: 'center' as const }

  const textElements: TextElement[] = [
    {
      text: userName,
      fontSize: nameFontSize,
      font: 'Helvetica',
      fontStyle: 'bold',
      color: NAME_MINT_SHADOW,
      y: nameY + PDF_NAME_SHADOW_OFFSET,
      x: nameX,
      options: nameOptions
    },
    {
      text: userName,
      fontSize: nameFontSize,
      font: 'Helvetica',
      fontStyle: 'bold',
      color: overlayColor,
      y: nameY,
      x: nameX,
      options: nameOptions
    },
    {
      text: formattedDate,
      fontSize: PDF_TEXT_FONT_SIZE,
      color: overlayColor,
      y: dateY,
      x: dateX,
      options: { align: 'center' }
    },
    {
      text: input.referenceNumber,
      fontSize: PDF_TEXT_FONT_SIZE,
      color: overlayColor,
      y: dateY,
      x: referenceX,
      options: { align: 'center' }
    }
  ]

  if (input.variant === 'enterprise' && typeof input.institutionName === 'string' && input.institutionName.length > 0) {
    textElements.push({
      text: input.institutionName.toUpperCase(),
      fontSize: PDF_INSTITUTION_FONT_SIZE,
      color: overlayColor,
      y: overlayPctToPdf(layout.institution.yPct, pdfPageHeight, PDF_INSTITUTION_FONT_SIZE),
      x: overlayPctToPdf(layout.institution.xPct, pdfPageWidth),
      options: { align: 'center' }
    })
  }

  return textElements
}

export async function generateCertificatePdf (input: CertificateInput): Promise<string> {
  const year = getCertificateYear(input.completedAt)
  const certUrl = getCertificateBackgroundUrl(input.variant, year)
  const overlayColor = getOverlayColor(year)

  let imageBase64: string
  try {
    const response = await fetch(certUrl)
    const blob = await response.blob()
    imageBase64 = await blobToDataUrl(blob)
  } catch (err) {
    logger.error(err as object)
    toast.error('Error occurred when creating the PDF document.')
    throw err
  }

  try {
    await waitForImage(imageBase64)
  } catch (err) {
    logger.error(err as object)
    toast.error('Error occurred when reading the PDF document.')
    throw err
  }

  const doc = new JsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [PDF_PAGE_SHORT_SIDE, PDF_PAGE_LONG_SIDE]
  })

  const pdfWidth: number = doc.internal.pageSize.getWidth()
  const pdfHeight = doc.internal.pageSize.getHeight()
  doc.addImage(imageBase64, 'JPG', 0, 0, pdfWidth, pdfHeight)

  buildTextElements(input, overlayColor, pdfWidth, pdfHeight, pdfMeasureNameWidth(doc)).forEach(textElement => {
    addTextToPDF(doc, textElement)
  })

  const pdfBlob = doc.output('blob')
  return window.URL.createObjectURL(pdfBlob)
}

export function getCachedCertificatePdfUrl (): string | null {
  return cache?.url ?? null
}

export function cleanupCertificatePdfUrl (): void {
  if (cache !== null) {
    window.URL.revokeObjectURL(cache.url)
    cache = null
  }
}

export async function generateAndCacheCertificatePdf (input: CertificateInput): Promise<string> {
  const url = await generateCertificatePdf(input)
  // Revoke only once the replacement exists, so the old URL stays downloadable while
  // a regeneration is in flight and concurrent calls cannot orphan a blob.
  cleanupCertificatePdfUrl()
  cache = { url, key: certificateInputKey(input) }
  return url
}

/**
 * Reuses the cached PDF when it was built from the same input, joins an in-flight
 * generation for that input, and otherwise builds a new one.
 */
export async function resolveCertificatePdfUrl (input: CertificateInput): Promise<string> {
  const key = certificateInputKey(input)
  if (cache !== null && cache.key === key) {
    return cache.url
  }
  if (pending !== null && pending.key === key) {
    return await pending.promise
  }

  const promise = generateAndCacheCertificatePdf(input)
  pending = { key, promise }
  try {
    return await promise
  } finally {
    if (pending?.promise === promise) {
      pending = null
    }
  }
}

export function triggerCertificateDownload (pdfUrl: string): void {
  const link = document.createElement('a')
  link.href = pdfUrl
  link.download = 'certificate.pdf'
  link.click()
}

/** Builds a throwaway PDF and downloads it without touching the cache. */
export async function downloadCertificateFromInput (input: CertificateInput): Promise<void> {
  const url = await generateCertificatePdf(input)
  triggerCertificateDownload(url)
  window.URL.revokeObjectURL(url)
}

export async function downloadCertificate (
  userProfile: UserProfile | null,
  institution: Institution | null,
  currentAttempt: Attempt | null
): Promise<void> {
  const input = buildCertificateInput(userProfile, institution, currentAttempt)
  if (input === null) {
    return
  }
  triggerCertificateDownload(await resolveCertificatePdfUrl(input))
}
